import {
  Card,
  CardBody,
  CardHeader,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerContentProps,
  DrawerFooter,
  DrawerHeader,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import Decimal from 'decimal.js';
import {
  ClipText,
  CurrencyLabel,
  DateDisplay,
  FormButtonGroup,
} from 'features/components';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AfeFormRequest, AfeFormWorkflowStage } from './AfeFormWorkflowDrawer';
import { PostAfeTransactionRequest } from 'api/bindings/PostAfeTransactionRequest';

export interface AfeFormReviewDrawerContentProps
  extends Omit<DrawerContentProps, 'onSubmit'> {
  initialStage: AfeFormWorkflowStage;
  // afe: AfeResponse;
  afe: AfeFormRequest;
  revision: PostAfeTransactionRequest | undefined;
  onSubmit: (form: Form) => void;
  onClose: () => void;
}

interface Form {
  nextStage: AfeFormWorkflowStage | undefined;
}

export const AfeFormReviewDrawerContent = (
  props: AfeFormReviewDrawerContentProps
) => {
  const { initialStage, afe, revision, onSubmit, onClose, ...rest } = props;

  // Fetches
  //   const auth = useAuth();
  //   const { data: subAccounts } = useFetchAccountsQuery(
  //     { clientId: auth?.user.clientId ?? 0 },
  //     { skip: !auth?.user.clientId }
  //   );
  // State
  const form = useForm<Form>({ mode: 'all' });
  const { handleSubmit } = form;

  const stats = useMemo(() => {
    return {
      lineCount: revision?.lineItems.length,
      subAccountCount: new Set(revision?.lineItems.map((x) => x.subAccountId)),
      budgeted: revision?.lineItems.reduce(
        (sum, n) => sum.add(n.budgetedAmount),
        new Decimal(0)
      ),
      contingency: revision?.lineItems.reduce(
        (sum, n) => sum.add(n.contingencyAmount),
        new Decimal(0)
      ),
    };
  }, [revision]);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DrawerContent {...rest}>
          <DrawerCloseButton />
          <DrawerHeader>Review AFE Changes</DrawerHeader>
          <DrawerBody>
            <Stack spacing={4}>
              {initialStage === AfeFormWorkflowStage.afe && (
                <Card>
                  <CardHeader>AFE: {afe.name}</CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={4}>
                      <Text as="b">Description:</Text>
                      <ClipText text={afe.description ?? ''} />
                      <Text as="b">Year:</Text>
                      <Text>{afe.year}</Text>
                      <Text as="b">Est. Start:</Text>
                      <DateDisplay value={afe.estimatedStartDate} />
                      <Text as="b">Est. End:</Text>
                      <DateDisplay value={afe.estimatedEndDate} />
                      <Text as="b">Actual Start:</Text>
                      <DateDisplay value={afe.actualStartDate} />
                      <Text as="b">Actual End:</Text>
                      <DateDisplay value={afe.actualEndDate} />
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
              {revision && (
                <Card>
                  <CardHeader>Revision: {revision.name}</CardHeader>
                  <CardBody>
                    <SimpleGrid columns={2} gap={4}>
                      <Text as="b">Line Items:</Text>
                      <Text>{stats.lineCount}</Text>
                      <Text as="b">Accounts:</Text>
                      <Text>{stats.subAccountCount.size}</Text>
                      <Text as="b">Budget:</Text>
                      <CurrencyLabel value={stats.budgeted} />
                      <Text as="b">Contingency:</Text>
                      <CurrencyLabel value={stats.contingency} />
                    </SimpleGrid>
                  </CardBody>
                </Card>
              )}
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              disabledWhenClean={false}
              onCancel={onClose}
              onConfirm={handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </form>
    </FormProvider>
  );
};
